import { useState, Fragment } from 'react';
import { formatNumber, formatDateTime, formatVersion, formatMs } from '../../utils/formatters';

/**
 * DeveloperView — Build-version-centric crash & ANR dashboard.
 * Shows crash/ANR stats per build version with expandable drill-down into
 * individual crash issues, stack traces, and AI fix recommendations.
 */

// ─── Build Version Row (main table) ─────────────────────────────────────────
function BuildRow({ build, isExpanded, onToggle }) {
  const crashSeverity = build.total_crashes > 50 ? 'text-red-400' :
    build.total_crashes > 10 ? 'text-amber-400' : 'text-emerald-400';
  const anrSeverity = build.anr_rate > 0.47 ? 'text-red-400' :
    build.anr_rate > 0.1 ? 'text-amber-400' : 'text-emerald-400';

  return (
    <Fragment>
      <tr
        onClick={onToggle}
        className="border-b border-surface-700/50 hover:bg-surface-700/40 cursor-pointer transition-colors group"
      >
        <td className="py-3 px-3 text-gray-500 w-8">
          {isExpanded ? '▼' : '▶'}
        </td>
        <td className="py-3 px-3">
          <span className="font-mono font-bold text-gray-100 group-hover:text-brand-400 transition-colors">
            {build.display_version}
          </span>
        </td>
        <td className="py-3 px-3">
          <span className={`font-mono font-bold text-sm ${crashSeverity}`}>
            {formatNumber(build.total_crashes)}
          </span>
        </td>
        <td className="py-3 px-3">
          <span className={`font-mono font-bold text-sm ${anrSeverity}`}>
            {formatNumber(build.total_anrs)}
          </span>
        </td>
        <td className="py-3 px-3">
          <span className={`text-xs font-medium ${build.crash_free_pct >= 99 ? 'text-emerald-400' : build.crash_free_pct >= 95 ? 'text-amber-400' : 'text-red-400'}`}>
            {build.crash_free_pct.toFixed(2)}%
          </span>
        </td>
        <td className="py-3 px-3">
          <span className={`text-xs font-medium ${anrSeverity}`}>
            {build.anr_rate.toFixed(3)}%
          </span>
        </td>
        <td className="py-3 px-3 text-xs text-gray-400">
          {formatNumber(build.total_users)}
        </td>
        <td className="py-3 px-3 text-xs text-gray-400">
          {formatNumber(build.total_sessions)}
        </td>
        <td className="py-3 px-3">
          <span className="text-[10px] text-gray-500 bg-surface-600/50 px-1.5 py-0.5 rounded">
            {build.crash_issues?.issue_count || 0} issues
          </span>
        </td>
      </tr>

      {/* Expanded: Crash issues for this build version */}
      {isExpanded && (
        <tr className="bg-surface-800/70">
          <td colSpan={9} className="px-4 py-4">
            <BuildDrillDown build={build} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// ─── Drill-down panel inside expanded row ────────────────────────────────────
function BuildDrillDown({ build }) {
  const [expandedIssue, setExpandedIssue] = useState(null);
  const issues = build.crash_issues?.issues || [];

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center gap-4 text-xs">
        <span className="text-gray-400">
          <span className="font-semibold text-gray-200">{build.display_version}</span> —
          {' '}{build.crash_issues?.issue_count || 0} unique issues, {' '}
          {formatNumber(build.crash_issues?.total_crash_events || build.total_crashes)} total crashes, {' '}
          {formatNumber(build.crash_issues?.affected_users || 0)} affected users
        </span>
      </div>

      {/* Crash Issues Table */}
      {issues.length > 0 ? (
        <div className="bg-surface-900/50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-700/50">
                <th className="w-6" />
                <th className="text-left py-2 px-3 text-[10px] text-gray-500 uppercase">Issue</th>
                <th className="text-left py-2 px-3 text-[10px] text-gray-500 uppercase">Crashes</th>
                <th className="text-left py-2 px-3 text-[10px] text-gray-500 uppercase">Users</th>
                <th className="text-left py-2 px-3 text-[10px] text-gray-500 uppercase">Last Seen</th>
                <th className="text-left py-2 px-3 text-[10px] text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((issue, idx) => (
                <Fragment key={issue.id || idx}>
                  <tr
                    onClick={() => setExpandedIssue(expandedIssue === idx ? null : idx)}
                    className="border-b border-surface-700/30 hover:bg-surface-700/30 cursor-pointer"
                  >
                    <td className="py-2 px-2 text-gray-600 text-xs">
                      {expandedIssue === idx ? '▼' : '▶'}
                    </td>
                    <td className="py-2 px-3">
                      <p className="text-xs text-gray-200 truncate max-w-md">{issue.title}</p>
                      {issue.subtitle && <p className="text-[10px] text-gray-600 truncate">{issue.subtitle}</p>}
                    </td>
                    <td className="py-2 px-3">
                      <span className="text-xs font-mono font-bold text-red-400">{formatNumber(issue.crash_count)}</span>
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-400">
                      {formatNumber(issue.user_count)}
                    </td>
                    <td className="py-2 px-3 text-[10px] text-gray-500">
                      {formatDateTime(issue.last_occurrence)}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                        issue.status === 'new' ? 'bg-red-500/15 text-red-400' :
                        issue.status === 'investigating' ? 'bg-amber-500/15 text-amber-400' :
                        'bg-emerald-500/15 text-emerald-400'
                      }`}>
                        {issue.status || 'new'}
                      </span>
                    </td>
                  </tr>

                  {/* Stack trace + device info */}
                  {expandedIssue === idx && (
                    <tr className="bg-surface-900/80">
                      <td colSpan={6} className="px-6 py-3">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                          <div>
                            <h5 className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Stack Trace</h5>
                            <pre className="text-[11px] text-gray-300 bg-surface-900 rounded-lg p-3 overflow-x-auto max-h-40 font-mono leading-relaxed">
                              {issue.stack_trace || 'No stack trace available'}
                            </pre>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <h5 className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Affected Devices</h5>
                              <div className="flex gap-1 flex-wrap">
                                {(issue.device_models || []).slice(0, 8).map(d => (
                                  <span key={d} className="text-[10px] bg-surface-600 text-gray-300 px-1.5 py-0.5 rounded">{d}</span>
                                ))}
                                {(!issue.device_models || issue.device_models.length === 0) && (
                                  <span className="text-[10px] text-gray-600">No device data</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <h5 className="text-[10px] font-semibold text-gray-400 uppercase mb-1">OS Versions</h5>
                              <div className="flex gap-1 flex-wrap">
                                {(issue.os_versions || []).slice(0, 8).map(os => (
                                  <span key={os} className="text-[10px] bg-surface-600 text-gray-300 px-1.5 py-0.5 rounded">Android {os}</span>
                                ))}
                              </div>
                            </div>
                            <div className="flex gap-4">
                              <div>
                                <h5 className="text-[10px] font-semibold text-gray-400 uppercase mb-0.5">First Seen</h5>
                                <p className="text-[11px] text-gray-300">{formatDateTime(issue.first_occurrence)}</p>
                              </div>
                              <div>
                                <h5 className="text-[10px] font-semibold text-gray-400 uppercase mb-0.5">Last Seen</h5>
                                <p className="text-[11px] text-gray-300">{formatDateTime(issue.last_occurrence)}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-gray-500 py-2">No individual crash issues tracked for this build yet. Total crashes from daily_metrics: {formatNumber(build.total_crashes)}</p>
      )}
    </div>
  );
}

// ─── AI Recommendation Card (Expandable) ───────────────────────────────────
function AIRecommendationCard({ rec, isExpanded, onToggle }) {
  const impactColor = (rec.impact || 0) >= 8 ? 'text-red-400 bg-red-500/10' :
    (rec.impact || 0) >= 5 ? 'text-amber-400 bg-amber-500/10' : 'text-blue-400 bg-blue-500/10';

  const severityColor = rec.severity === 'critical' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
    rec.severity === 'high' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
    'bg-blue-500/20 text-blue-400 border-blue-500/30';

  const effortColor = rec.effort === 'low' ? 'text-emerald-400 bg-emerald-500/10' :
    rec.effort === 'medium' ? 'text-amber-400 bg-amber-500/10' : 'text-red-400 bg-red-500/10';

  return (
    <div
      onClick={onToggle}
      className={`bg-surface-700/40 border rounded-lg cursor-pointer transition-all hover:border-brand-500/30 ${
        isExpanded ? 'border-brand-500/40 ring-1 ring-brand-500/20' : 'border-surface-600/50'
      }`}
    >
      {/* Header — always visible */}
      <div className="p-3">
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded bg-brand-500/20 flex items-center justify-center shrink-0 mt-0.5">
            <svg className="w-3 h-3 text-brand-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="text-sm font-medium text-gray-200">{rec.title}</p>
              {rec.severity && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${severityColor}`}>
                  {rec.severity === 'critical' ? 'P0' : rec.severity === 'high' ? 'P1' : 'P2'}
                </span>
              )}
              {rec.category && (
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-surface-600/60 text-gray-400">
                  {rec.category}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 line-clamp-2">{rec.description || rec.detail}</p>

            {/* Quick stat pills */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {rec.impact != null && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${impactColor}`}>
                  Impact: {rec.impact}/10
                </span>
              )}
              {rec.effort && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${effortColor}`}>
                  Effort: {rec.effort}
                </span>
              )}
              {rec.postFixGain > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
                  +{rec.postFixGain} health pts
                </span>
              )}
              {rec.confidence && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-surface-600/60 text-gray-400">
                  {rec.confidence}% confidence
                </span>
              )}
            </div>
          </div>
          <span className="text-gray-600 text-xs shrink-0 mt-1">
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
      </div>

      {/* Expanded Detail — Root Cause, Code Hints, Impact */}
      {isExpanded && (
        <div className="border-t border-surface-600/50 p-3 space-y-3 bg-surface-800/40 rounded-b-lg">
          {/* Root Cause Analysis */}
          {rec.rootCause && (
            <div>
              <h5 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Root Cause Analysis
              </h5>
              <p className="text-xs text-gray-300 leading-relaxed">{rec.rootCause}</p>
            </div>
          )}

          {/* Fix Recommendation */}
          {rec.description && (
            <div>
              <h5 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Recommended Fix
              </h5>
              <p className="text-xs text-gray-300 leading-relaxed">{rec.description}</p>
            </div>
          )}

          {/* Code Hints */}
          {rec.codeHints && rec.codeHints.length > 0 && (
            <div>
              <h5 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <svg className="w-3 h-3 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                Code-Level Fixes
              </h5>
              <div className="space-y-1.5">
                {rec.codeHints.map((hint, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-[10px] font-bold text-brand-400 bg-brand-500/10 px-1.5 py-0.5 rounded shrink-0">{i + 1}</span>
                    <p className="text-xs text-gray-300 font-mono leading-relaxed">{hint}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Estimated Impact */}
          {rec.estimatedImpact && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2.5">
              <h5 className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Estimated Impact
              </h5>
              <p className="text-xs text-emerald-300/80 leading-relaxed">{rec.estimatedImpact}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main DeveloperView Component ───────────────────────────────────────────
export default function DeveloperView({ crashes = [], heatmap = [], anr = {}, aiRecs = [], devSummary, buildBreakdown = [] }) {
  const [expandedBuild, setExpandedBuild] = useState(null);
  const [expandedRec, setExpandedRec] = useState(null);
  const [sortBy, setSortBy] = useState('total_crashes');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const sorted = [...buildBreakdown].sort((a, b) => {
    const mul = sortDir === 'asc' ? 1 : -1;
    return ((a[sortBy] || 0) > (b[sortBy] || 0) ? 1 : -1) * mul;
  });

  const SortHeader = ({ col, children }) => (
    <th
      onClick={() => handleSort(col)}
      className="text-left py-2.5 px-3 text-[10px] text-gray-500 uppercase tracking-wider font-semibold cursor-pointer hover:text-gray-300 select-none whitespace-nowrap"
    >
      {children}
      {sortBy === col && <span className="ml-1 text-brand-400">{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <div className="space-y-4">
      {/* Quick Stats */}
      {devSummary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Total Crashes', value: formatNumber(devSummary.totalCrashes || 0), color: 'text-red-400', border: 'border-red-500/30' },
            { label: 'Total ANRs', value: formatNumber(devSummary.totalAnrs || 0), color: 'text-amber-400', border: 'border-amber-500/30' },
            { label: 'Crash Issues', value: formatNumber(devSummary.crashIssues?.total || 0), color: 'text-purple-400', border: 'border-purple-500/30' },
            { label: 'Affected Users', value: formatNumber(devSummary.totalUsers || 0), color: 'text-blue-400', border: 'border-blue-500/30' },
            { label: 'Health Score', value: devSummary.healthScore || '—', color: 'text-emerald-400', border: 'border-emerald-500/30' },
          ].map(s => (
            <div key={s.label} className={`bg-surface-700/40 rounded-xl border-l-4 ${s.border} border border-surface-600/50 p-3`}>
              <p className="text-[10px] text-gray-500 uppercase">{s.label}</p>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* AI Recommendations — Clickable with full detail */}
      {aiRecs.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 rounded-md bg-brand-500/20 flex items-center justify-center">
              <svg className="w-3 h-3 text-brand-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-200">AI Impact & Fix Recommendations</h3>
            <span className="text-[10px] text-gray-600 bg-surface-600/50 px-1.5 py-0.5 rounded">{aiRecs.length}</span>
            <span className="text-[10px] text-gray-500 ml-auto">Click to expand details</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {aiRecs.map((rec, i) => (
              <AIRecommendationCard
                key={rec.id || i}
                rec={rec}
                isExpanded={expandedRec === (rec.id || i)}
                onToggle={() => setExpandedRec(expandedRec === (rec.id || i) ? null : (rec.id || i))}
              />
            ))}
          </div>
        </div>
      )}

      {/* Build Version Breakdown Table — MAIN FEATURE */}
      <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-600/50">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-red-500/20 flex items-center justify-center">
              <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-200">Crash & ANR by Build Version</h3>
            <span className="text-[10px] text-gray-600 bg-surface-600/50 px-1.5 py-0.5 rounded">
              {buildBreakdown.length} builds · Click row to drill down
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-600/50">
                <th className="w-8" />
                <SortHeader col="app_version">Build Version</SortHeader>
                <SortHeader col="total_crashes">Crashes</SortHeader>
                <SortHeader col="total_anrs">ANRs</SortHeader>
                <SortHeader col="crash_free_pct">Crash-Free %</SortHeader>
                <SortHeader col="anr_rate">ANR Rate</SortHeader>
                <SortHeader col="total_users">Users</SortHeader>
                <SortHeader col="total_sessions">Sessions</SortHeader>
                <th className="text-left py-2.5 px-3 text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Issues</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((build, i) => (
                <BuildRow
                  key={build.app_version + '-' + i}
                  build={build}
                  isExpanded={expandedBuild === build.app_version}
                  onToggle={() => setExpandedBuild(expandedBuild === build.app_version ? null : build.app_version)}
                />
              ))}
            </tbody>
          </table>
          {sorted.length === 0 && (
            <p className="text-center text-gray-500 py-8 text-sm">No build data available. Run a sync to fetch latest data from Firebase.</p>
          )}
        </div>
      </div>

      {/* ANR Summary (if data) */}
      {(anr.byType?.length > 0 || anr.byOs?.length > 0) && (
        <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 p-4">
          <h3 className="text-sm font-semibold text-gray-200 mb-3">ANR Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {anr.byType?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">By Device Type</h4>
                {anr.byType.slice(0, 6).map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-surface-700/50 last:border-0">
                    <span className="text-sm text-gray-300 truncate">{a.device_type || a.type || a.anr_type}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-amber-400">{formatNumber(a.total_anr || a.count || a.anr_count)}</span>
                      {a.anr_rate && <span className="text-[10px] text-gray-500">{parseFloat(a.anr_rate).toFixed(2)}%</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {anr.byOs?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">By OS Version</h4>
                {anr.byOs.slice(0, 6).map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-surface-700/50 last:border-0">
                    <span className="text-sm text-gray-300">Android {a.os_version}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-gray-400">{formatNumber(a.total_anr || a.count || a.anr_count)}</span>
                      {a.anr_rate && <span className="text-[10px] text-gray-500">{parseFloat(a.anr_rate).toFixed(2)}%</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
